/* eslint-disable react/no-unsafe */
/**
 * A button encircled by a countdown ring, à la YouTube.
 *
 * The SVG itself doesn't use any pixel values, just percentages. This lets us
 * inherit the parent's size if we want to!
 */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedIcon, {
    type IconType,
} from "#shared-styles-package/deprecated-icon.tsx";

type DefaultProps = {
    // Background color.
    background: string;
    // Allows for a parent component to easily cancel the `onComplete`
    // callback. The countdown ceases as soon as this becomes truthy, and
    // never begins if this is initially truthy. A fresh countdown can be
    // started by setting this back to falsey.
    cancelled: boolean;
    // The amount of time in milliseconds until `onComplete` is triggered.
    duration: number;
    // Icon and ring color.
    foreground: string;
    // Size from [0, 1], as a proportion of our full button size.
    iconSize: number;
    // Proportion of our button ([0, 1]) that should be filled by the ring.
    ringWeight: number;
    // AKA diameter. You probably don't need to set this; the button
    // defaults to fit its container. All standard CSS units are supported.
    size: number | string;
};

type Props = DefaultProps & {
    // An SVG path or icon object, as can be found in `icon-paths.jsx`.
    icon: IconType | string;
    // Called when the button is clicked or the countdown completes.
    onComplete: (
        e?: React.SyntheticEvent | null | undefined,
        triggeredByTimer?: boolean,
    ) => unknown;
    // aria-label to apply to the SVG button
    ariaLabel?: string;
};

type State = {
    initialStartTime: number;
    offset: number;
    startTime: number;
};

type Options = {
    blockCompletionCallback?: boolean;
    triggeredByTimer?: boolean;
};

class CountdownButton extends React.Component<Props, State> {
    // Keep a reference to the current frame to prevent overlapping animations.
    // We keep this outside of state to ensure synchronous updates.
    // @ts-expect-error - TS2564 - Property '_currentAnimationFrame' has no initializer and is not definitely assigned in the constructor.
    _currentAnimationFrame: number;

    static defaultProps: DefaultProps = {
        background: "#000",
        cancelled: false,
        duration: 10000, // 10 seconds.
        foreground: "#fff",
        iconSize: 0.3,
        ringWeight: 0.1,
        size: "100%",
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            // Not super important, but we don't need to wait until the component
            // actually mounts on page to start the clock.
            initialStartTime: performance.now(),
            offset: 0,
            startTime: 0,
        };
    }

    componentDidMount() {
        if (!this.props.cancelled) {
            this.startCountdown(this.state.initialStartTime);
        }
    }

    UNSAFE_componentWillReceiveProps({cancelled: nextCancelled}: Props) {
        const {cancelled} = this.props;

        if (cancelled !== nextCancelled) {
            if (nextCancelled) {
                this.handleComplete(null, {blockCompletionCallback: true});
            } else {
                this.startCountdown();
            }
        }
    }

    componentWillUnmount() {
        // TODO(jeff, LP-6954): Use Wonder Blocks Timing API.
        // eslint-disable-next-line no-restricted-syntax
        cancelAnimationFrame(this._currentAnimationFrame);
    }

    startCountdown: (startTime?: number) => void = (
        startTime: number = performance.now(),
    ) => {
        this.setState({startTime}, this.step);
    };

    handleComplete: (
        e?: React.SyntheticEvent | null | undefined,
        options?: Options,
    ) => void = (
        e?: React.SyntheticEvent | null,
        options: Options = {} as any,
    ) => {
        const {onComplete} = this.props;

        this.setState({offset: 0});
        // TODO(jeff, LP-6954): Use Wonder Blocks Timing API.
        // eslint-disable-next-line no-restricted-syntax
        cancelAnimationFrame(this._currentAnimationFrame);

        if (!options.blockCompletionCallback) {
            onComplete(e, options.triggeredByTimer);
        }
    };

    step: (t?: number) => void = (t = 0) => {
        const {duration} = this.props;
        const {startTime} = this.state;

        t = Math.max(0, t - startTime);
        if (t >= duration) {
            this.handleComplete(null, {triggeredByTimer: true});
        } else {
            this.setState({offset: t / duration - 1});
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API.
            // eslint-disable-next-line no-restricted-syntax
            this._currentAnimationFrame = requestAnimationFrame(this.step);
        }
    };

    render(): React.ReactNode {
        const {
            background,
            cancelled,
            foreground,
            icon,
            iconSize,
            ringWeight,
            size,
            ariaLabel,
        } = this.props;
        const {offset} = this.state;

        const ringRadius = (1 - ringWeight / 2) * 50;
        const ringCircumference = 2 * Math.PI * ringRadius;

        // TODO(riley): Handle icons that have an aspect ratio other than 1:1.
        const iconSizePercent = `${iconSize * 100}%`;
        const iconOffsetPercent = `${(1 - iconSize) * 50}%`;

        return (
            <svg
                className={css(styles.container)}
                height={size}
                onClick={this.handleComplete}
                version="1.1"
                width={size}
                aria-label={ariaLabel}
            >
                <circle cx="50%" cy="50%" fill={background} r="50%" />
                {/* Someday, `stroke-alignment` will land and this extra element
                won't be needed… */}
                {!cancelled && (
                    <circle
                        className={css(styles.ring)}
                        cx="50%"
                        cy="50%"
                        fill="transparent"
                        r={`${ringRadius}%`}
                        stroke={foreground}
                        strokeDasharray={`${ringCircumference}%`}
                        // The extra 3% puts the ring slightly outside of our inner
                        // circle so we don't get icky antialiasing artifacts.
                        strokeWidth={`${ringWeight * 53}%`}
                        style={{
                            strokeDashoffset: `${-ringCircumference * offset}%`,
                        }}
                    />
                )}
                <svg
                    height={iconSizePercent}
                    version="1.1"
                    viewBox="0 0 1 1"
                    width={iconSizePercent}
                    x={iconOffsetPercent}
                    y={iconOffsetPercent}
                >
                    <DeprecatedIcon icon={icon} size={1} color={foreground} />
                </svg>
            </svg>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        cursor: "pointer",
        display: "block",
        margin: "auto",
    },

    ring: {
        transform: "rotate(-90deg)",
        transformOrigin: "50% 50%",
    },
});

export default CountdownButton;
